/* eslint-disable no-nested-ternary */
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Button,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import FeatureModal from "src/components/pages/PricingPlans/FeaturesModal";
import { IPlan, IPrices, ISelectedProductDetails } from "../dataTypes";
import useStyles from "./Styles";

interface Props {
  selectedProductDetail: ISelectedProductDetails;
  plan: string;
  products: IPlan[];
  handlePackagePurchase: any;
  loader: boolean;
  setPlan: React.Dispatch<React.SetStateAction<string>>;
  setCalculatedCutPrice: any;
  setCalculatedPrice: any;
  setPerPeriodName: any;
  setAgentsNumber: any;
  agentsNumber: any;
  setPriceId: any;
}

const BillingPortal: React.FC<Props> = ({
  selectedProductDetail,
  setCalculatedCutPrice,
  setCalculatedPrice,
  setPerPeriodName,
  setAgentsNumber,
  agentsNumber,
  setPriceId,
  plan,
  products,
  loader,
  handlePackagePurchase,
  setPlan,
}) => {
  const { classes } = useStyles();
  // initially setting period based on plan coming from parent
  const [period, setPeriod] = useState(
    plan === "monthly" ? "month-1" : "year-1",
  );

  const [selectedInterval, setSelectedInterval] = useState({
    interval: plan === "monthly" ? "month" : "year",
    intervalCount: 1,
  });
  // initially setting yearsInNumber based on plan coming from parent

  useEffect(() => {
    setPeriod(plan === "monthly" ? "month-1" : "year-1");
    setSelectedInterval({
      interval: plan === "monthly" ? "month" : "year",
      intervalCount: 1,
    });
  }, [plan]);

  const [openFeatureModal, setOpenFeatureModal] = useState(false);

  const handlePlanChange = (e: any) => {
    setPeriod(e.target.value);

    const interval = e.target.value.split("-")[0];
    const intervalCount = Number(e.target.value.split("-")[1]);

    setSelectedInterval({
      interval,
      intervalCount,
    });
  };

  const getPricesAccordingToPlan = (pricesList: IPrices[]) => {
    const interval = selectedInterval.interval || "month";
    const intervalCount = selectedInterval.intervalCount || 1;

    if (interval === "month") {
      const montlyPrice = pricesList.find((item) => item.interval === "month");

      if (montlyPrice) {
        return Math.ceil(montlyPrice.price / 100);
      }
    }

    if (interval === "year") {
      const annualPrice = pricesList.find(
        (item) =>
          item.interval === "year" && item.intervalCount === intervalCount,
      );
      if (annualPrice) {
        return Math.ceil(annualPrice.price / 100 / (12 * intervalCount));
      }
    }

    return null;
  };

  const getPriceAccordingToPlanString = useMemo(
    () => getPricesAccordingToPlan(selectedProductDetail.price),
    [selectedInterval],
  );
  const calculatedCutPrice = () => {
    if (selectedInterval.interval === "month") {
      return 1;
    }

    const monthlyPrice =
      selectedProductDetail.price.find((curr) => curr.interval === "month")
        ?.price || 0;

    const cutPrice =
      (monthlyPrice / 100) * 12 * selectedInterval.intervalCount * agentsNumber;

    return cutPrice;
  };

  const calculatedPrice = () => {
    const packagePriceBasedOnIntervalAndCount =
      selectedProductDetail.price.find(
        (curr) =>
          curr.interval === selectedInterval.interval &&
          curr.intervalCount === selectedInterval.intervalCount,
      );

    if (packagePriceBasedOnIntervalAndCount) {
      return (packagePriceBasedOnIntervalAndCount.price / 100) * agentsNumber;
    }

    return 0;
  };

  const perPeriodName = () => {
    if (selectedInterval.interval === "month") return "mo";

    switch (selectedInterval.intervalCount) {
      case 1:
        return "yr";
        break;
      case 2:
        return "2yr";
        break;
      case 3:
        return "3yr";
        break;

      default:
        return "";
        break;
    }
  };

  const getPriceId = () => {
    const selectedPlanPriceObject = selectedProductDetail.price.find(
      (curr) =>
        curr.interval === selectedInterval.interval &&
        curr.intervalCount === selectedInterval.intervalCount,
    );
    if (selectedPlanPriceObject) {
      return selectedPlanPriceObject?.priceId;
    }
    return "";
  };

  useEffect(() => {
    setPriceId(getPriceId());
  }, [selectedInterval]);

  useEffect(() => {
    setCalculatedCutPrice(calculatedCutPrice());
    setCalculatedPrice(calculatedPrice());
    setPerPeriodName(perPeriodName());
  }, [selectedInterval, agentsNumber]);

  return (
    <>
      <FeatureModal
        open={openFeatureModal}
        setOpen={setOpenFeatureModal}
        loader={loader}
        products={products}
        handlePackagePurchase={handlePackagePurchase}
        plan={plan}
        setPlan={setPlan}
      />
      <Box sx={{ width: "100%" }}>
        <Box className={classes.leftContainer}>
          <Box className={classes.subscriptionHeader}>
            <Typography className={classes.planText}>
              {selectedProductDetail.productName} plan
            </Typography>
            <Box className={classes.pricePerIntevalBox}>
              <Typography className={classes.pricePerIntevalText}>
                ${getPriceAccordingToPlanString} / month
              </Typography>
              <Typography className={classes.pricePerIntevalText2}>
                per agent
              </Typography>
            </Box>
          </Box>
          <Typography className={classes.fieldStyle}>Select Bill</Typography>
          <Box sx={{ display: "flex", gap: "15px", mb: "15px" }}>
            <FormControl fullWidth>
              <Select
                labelId="interval"
                id="interval"
                value={period}
                // label="Select Plan"
                onChange={handlePlanChange}
                sx={{
                  border: "1px solid #272525",
                  borderRadius: "5px",
                  "& fieldset": {
                    border: "none", // Remove default border from MUI
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #272525",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #272525",
                  },
                }}
              >
                <MenuItem value="month-1">Billed Monthly</MenuItem>
                <MenuItem value="year-1">Billed Annually</MenuItem>
                <MenuItem value="year-2">Billed Every 2 Years</MenuItem>
                <MenuItem value="year-3">Billed Every 3 Years</MenuItem>
              </Select>
            </FormControl>
            {period.toLowerCase() === "month-1" && (
              <Typography
                className={classes.saveAnnuallyText}
                onClick={() => setPeriod("year-1")}
              >
                Save $5
                <br />
                annually
              </Typography>
            )}
          </Box>
          {/* select agent Numbers */}
          <Typography className={classes.fieldStyle}>Select Agent</Typography>
          <Box display="flex" alignItems="center" mb="15px">
            <TextField
              type="number"
              variant="outlined"
              defaultValue={1}
              inputProps={{ min: 1 }}
              // fullWidth
              value={agentsNumber}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value));
                setAgentsNumber(value);
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "#272525",
                  },
                  "&:hover fieldset": {
                    borderColor: "#272525",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#272525",
                  },
                },
              }}
            />
            <Typography
              className={classes.noOfAgentsText}
              sx={{
                marginLeft: "8px",
              }}
            >
              {`agent${agentsNumber > 1 ? "s" : ""}`}
            </Typography>
          </Box>
          <Button
            className={classes.changePlanBtn}
            onClick={() => setOpenFeatureModal(true)}
          >
            Change plan
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default BillingPortal;
